import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Button, Collapse, DialogActions, DialogContent, Grid } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { TFormType, TMainVisualFormData } from 'src/@types';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import FormProvider, { RHFSelect, RHFSwitch } from 'src/components/hook-form';
import RHFTextFieldWithColor from 'src/components/hook-form/RHFTextFieldWithColor';
import RHFUploadImage from 'src/components/hook-form/uploadImage/RHFUploadImage';
import Iconify from 'src/components/iconify';
import Preview from 'src/components/preview/Preview';
import useGetGuideInfo from 'src/hooks/useGetGuideInfo';
import useGetSelectOption from 'src/hooks/useGetSelectOption';
import { ContentButtonAction } from 'src/layouts/form/FormButton';
import { FormInnerContainer, FormItem, FormLabel, FormRow } from 'src/layouts/form/FormLayout';
import { IMainVisual, ITextAttribute, useStores } from 'src/models';
import { mainVisualApi } from 'src/service';
import { useHandleResponse } from 'src/service/useHandleResponse';
import { FormType } from 'src/static/constants/common/form';
import { getMainVisualFormDefaultValues } from 'src/utils/getRHFDefaultValues';
import { separateFilesAndPaths } from 'src/utils/separateFilesAndPaths';

interface IMainVisualFormDialogProps {
  formType: TFormType;
  onSubmitSuccess: () => void;
  selectedData?: IMainVisual;
}

interface IPreviewValues {
  mainText: ITextAttribute;
  subText: ITextAttribute;
  bgPath: Array<File | string>;
  [key: string]: ITextAttribute | Array<File | string>;
}
const initialStyleValue = { text: '', color: '#000' };
export const DataMode = observer(
  ({ formType, onSubmitSuccess, selectedData }: IMainVisualFormDialogProps) => {
    const { t } = useTranslation();
    const handleResponse = useHandleResponse();
    const { alertStore } = useStores();
    const { formId } = useDialogContext();
    const { getLang } = useGetSelectOption();
    const { getMainVisualAreas } = useGetGuideInfo();
    // 가이드 보기 토글
    const [isGuideOpen, setIsGuideOpen] = useState(true);
    // 색상 상태
    const [colors, setColors] = useState({
      mainText: selectedData?.mainText?.color || '#000',
      subText: selectedData?.subText?.color || '#000',
    });

    const methods = useForm<TMainVisualFormData>({
      mode: 'all',
      reValidateMode: 'onChange',
      defaultValues: getMainVisualFormDefaultValues(selectedData),
    });

    console.log(getMainVisualFormDefaultValues(selectedData));

    const [files, setFiles] = useState<Array<File | string>>([]);

    // preview에 필요하여 폼 데이터 변경 감지를 위한 상태
    const [previewValues, setPreviewValues] = useState<IPreviewValues>({
      mainText: initialStyleValue,
      subText: initialStyleValue,
      bgPath: [],
    });

    useEffect(() => {
      if (formType === FormType.UPDATE && selectedData) {
        setPreviewValues({
          mainText: {
            text: selectedData.mainText?.text || '',
            color: selectedData.mainText?.color || '#000',
          },
          subText: {
            text: selectedData.subText?.text || '',
            color: selectedData.subText?.color || '#000',
          },
          bgPath: selectedData.bgPath || [],
        });
      }
    }, [formType, selectedData]);

    useEffect(() => {
      const subscription = methods.watch((values: any, { name }) => {
        if (name === 'mainText' || name === 'subText') {
          const fieldValue = values[name];
          setPreviewValues((prev) => ({
            ...prev,
            [name]: {
              text: fieldValue?.text || '',
              color: fieldValue?.color || colors[name],
            },
          }));
        } else if (name === 'bgPath') {
          setPreviewValues((prev) => ({
            ...prev,
            bgPath: values[name] || [],
          }));
        }
      });

      return () => subscription.unsubscribe();
    }, [methods]);

    useEffect(() => {
      setFiles(selectedData?.bgPath || []);
    }, []);

    // 등록 / 수정 함수
    const onValid = async (data: TMainVisualFormData) => {
      const isCreate = formType === FormType.CREATE;
      // `bgPath` 배열에서 파일과 경로 분리
      const { files: uploadFiles, paths: existingPaths } = separateFilesAndPaths(files || []);

      // 삭제된 경로 추출
      const originalPaths = selectedData?.bgPath || [];
      const deleteFilesPath = originalPaths.filter((path) => !existingPaths.includes(path));

      let formData: TMainVisualFormData = {
        ...(uploadFiles.length > 0 && { files: uploadFiles }),
        ...data,
      };

      if (!isCreate) {
        formData = {
          ...formData,
          visualSid: selectedData?.visualSid,
          ...(uploadFiles.length > 0 && { files: uploadFiles }),
          ...(existingPaths.length > 0 && { bgPath: existingPaths }),
          ...(deleteFilesPath.length > 0 && { deleteFilesPath }),
        };
      }

      const result = isCreate ? mainVisualApi.post(formData) : mainVisualApi.put(formData);
      if (await result) {
        handleResponse('완료되었습니다.', '실패되었습니다.', onSubmitSuccess);
      }
    };

    // 삭제
    const handleDelete = () => {
      alertStore.setOnConfirm(async () => {
        if (selectedData?.visualSid) {
          mainVisualApi
            .remove(selectedData?.visualSid)
            .then(() =>
              handleResponse('삭제가 완료되었습니다.', '삭제가 실패되었습니다.', onSubmitSuccess),
            );
        }
      });
      alertStore.setProps({
        open: true,
        title: '컨텐츠 삭제',
        content: '해당 컨텐츠를 삭제하시겠습니까?',
        category: 'error',
        hasCancelButton: true,
      });
    };

    // 파일 변경 핸들러 수정
    const handleFileChange = (newFiles: Array<File | string>) => {
      setFiles(newFiles);
      // previewValues의 bgPath도 업데이트
      setPreviewValues((prev) => ({
        ...prev,
        bgPath: newFiles,
      }));
    };

    // 토글
    const handleToggle = () => {
      setIsGuideOpen(!isGuideOpen);
    };

    // 색상 변경 핸들러
    const handleColorChange = (fieldName: string) => (color: string) => {
      setColors((prev) => ({
        ...prev,
        [fieldName]: color,
      }));

      // preview 값도 업데이트
      setPreviewValues((prev) => ({
        ...prev,
        [fieldName]: {
          ...prev[fieldName],
          color,
        },
      }));
    };

    return (
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onValid)} formId={formId}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ justifyContent: 'flex-end', display: 'flex', mb: 2 }}>
                <ContentButtonAction onClick={handleToggle}>
                  <Iconify icon="mdi:help-circle-outline" sx={{ mr: 1 }} /> 미리 보기
                </ContentButtonAction>
              </Box>

              <Collapse in={isGuideOpen}>
                <Box sx={{ mb: 3 }}>
                  <Preview
                    formType={formType}
                    areas={getMainVisualAreas()}
                    values={previewValues}
                  />
                </Box>
              </Collapse>

              <FormRow sx={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                <FormItem>
                  <RHFSwitch label={t('mainVisual.form.useYn')} name={'useYn'} />
                </FormItem>
              </FormRow>
              <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                <FormItem>
                  <RHFSelect
                    label={t('mainVisual.form.lang')}
                    name={'lang'}
                    useAll={false}
                    options={getLang()}
                  />
                </FormItem>
              </FormRow>
              <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                <FormItem>
                  <RHFTextFieldWithColor
                    name="mainText"
                    label={t('mainVisual.form.mainText')}
                    onColorChange={handleColorChange('mainText')}
                    selectedColor={colors.mainText}
                  />
                </FormItem>
              </FormRow>
              <FormRow sx={{ gridTemplateColumns: '1fr' }}>
                <FormItem>
                  <RHFTextFieldWithColor
                    name="subText"
                    label={t('mainVisual.form.subText')}
                    onColorChange={handleColorChange('subText')}
                    selectedColor={colors.subText}
                  />
                </FormItem>
              </FormRow>
              <FormInnerContainer>
                <FormLabel>{t('mainVisual.form.background')}</FormLabel>
                <FormRow
                  sx={{
                    gridTemplateColumns: '1fr',
                    overflow: 'visible',
                  }}
                >
                  <FormItem>
                    <RHFUploadImage
                      name={'bgPath'}
                      files={files}
                      onChange={handleFileChange}
                      accept={{
                        'image/*': [],
                        'video/mp4': [],
                        'video/webm': [],
                      }}
                    />
                  </FormItem>
                </FormRow>
              </FormInnerContainer>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <>
            {formType === FormType.UPDATE && (
              <Button onClick={handleDelete} variant="contained" color="error">
                {t('common.button.delete')}
              </Button>
            )}
            <Button type="submit" variant="contained" form={formId} color="primary">
              {formType === FormType.CREATE
                ? `${t('common.button.register')}`
                : `${t('common.button.edit')}`}
            </Button>
          </>
        </DialogActions>
      </FormProvider>
    );
  },
);
